'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["active", "block", "children", "className", "disabled", "flush", "height", "hovered", "innerClassName", "name", "onClick", "onSelectedChange", "padded", "readOnly", "selected", "selectionMarkLocation", "tabIndex", "type", "width"],
  _excluded2 = ["active"],
  _excluded3 = ["hovered"];
import devLogger from 'react-utils/devLogger';
import PropTypes from 'prop-types';
import { Component } from 'react';
import classNames from 'classnames';
import Controllable from '../decorators/Controllable';
import SythenticEvent from '../core/SyntheticEvent';
import UICheckbox from '../input/UICheckbox';
import UIRadioInput from '../input/UIRadioInput';
import { uniqueId } from '../utils/underscore';
import { stopPropagationHandler } from '../utils/DomEvents';
import ActiveProvider from '../providers/ActiveProvider';
import HoverProvider from '../providers/HoverProvider';
import { hidden } from '../utils/propTypes/decorators';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
class UISelectableBox extends Component {
  constructor(props) {
    super(props);
    this.handleClick = evt => {
      const {
        onClick,
        onSelectedChange,
        selected,
        type,
        disabled,
        readOnly
      } = this.props;
      if ((disabled || readOnly) && evt != null) {
        evt.preventDefault();
        evt.stopPropagation();
        return;
      }
      if (type !== 'radio') {
        onSelectedChange(SythenticEvent(!selected));
      }
      if (onClick && !disabled) onClick(evt);
    };
    this.handleKeyPress = evt => {
      const {
        disabled
      } = this.props;
      if (evt.target.type === 'checkbox') return;
      const {
        key
      } = evt;
      // treat enter or space key like a click event
      if ((key === 'Enter' || key === ' ') && disabled !== true) {
        evt.preventDefault();
        this.handleClick();
      }
    };
    this._id = uniqueId('bsb-');
  }
  render() {
    const _this$props = this.props,
      {
        block,
        children,
        className,
        disabled,
        flush,
        height,
        innerClassName,
        name,
        padded,
        readOnly,
        selected,
        selectionMarkLocation,
        tabIndex,
        type,
        width
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    return /*#__PURE__*/_jsx(HoverProvider, Object.assign({}, this.props, {
      children: hoverProviderProps => /*#__PURE__*/_jsx(ActiveProvider, Object.assign({}, this.props, {
        children: activeProviderProps => {
          const {
              active
            } = activeProviderProps,
            activeProviderRestProps = _objectWithoutPropertiesLoose(activeProviderProps, _excluded2);
          const {
              hovered
            } = hoverProviderProps,
            hoverProviderRestProps = _objectWithoutPropertiesLoose(hoverProviderProps, _excluded3);
          const computedClassName = classNames("uiButton private-button private-selectable-box", className, disabled ? 'disabled private-button--disabled' : hovered && !readOnly && 'private-selectable-box--hovered', active && 'private-button--active', selected && 'private-selectable-box--selected', block && 'private-button--block', padded && 'private-selectable-box--padded', flush && 'private-selectable-box--flush', readOnly && 'private-selectable-box--readonly');
          let renderedSelectionMark = null;
          if (type !== 'default') {
            const selectionMarkClassname = classNames('private-selectable-box__selection-mark', {
              'left': 'private-selectable-box__selection-mark--left',
              'top-right': 'private-selectable-box__selection-mark--top-right'
            }[selectionMarkLocation]);
            if (type === 'checkbox') {
              renderedSelectionMark = /*#__PURE__*/_jsx("div", {
                className: selectionMarkClassname,
                children: /*#__PURE__*/_jsx(UICheckbox, {
                  "aria-labelledby": this._id,
                  checked: selected,
                  disabled: disabled,
                  innerPadding: "none",
                  name: name,
                  onChange: this.handleClick,
                  onClick: stopPropagationHandler,
                  readOnly: readOnly
                })
              });
            }
            if (type === 'radio') {
              if (process.env.NODE_ENV !== 'production' && !name) {
                devLogger.warn({
                  message: 'UISelectableBox: If type is set to radio you must provide a name',
                  key: 'UISelectableBox: no name value'
                });
              }
              renderedSelectionMark = /*#__PURE__*/_jsx("div", {
                className: selectionMarkClassname,
                children: /*#__PURE__*/_jsx(UIRadioInput, {
                  "aria-labelledby": this._id,
                  checked: selected,
                  disabled: disabled,
                  name: name,
                  onChange: this.handleClick,
                  onClick: stopPropagationHandler,
                  readOnly: readOnly
                })
              });
            }
          }
          const tabbableByDefault = !disabled && type === 'default';
          const defaultTabIndex = tabbableByDefault ? 0 : null;
          return (
            /*#__PURE__*/
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions
            _jsxs("div", Object.assign({}, rest, activeProviderRestProps, hoverProviderRestProps, {
              "aria-checked": selected,
              "aria-disabled": disabled,
              className: computedClassName,
              onClick: this.handleClick,
              onKeyPress: this.handleKeyPress,
              role: type === 'radio' ? 'radio' : 'checkbox',
              tabIndex: typeof tabIndex === 'number' ? tabIndex : defaultTabIndex,
              children: [renderedSelectionMark, /*#__PURE__*/_jsx("div", {
                className: classNames('private-selectable-box__inner', innerClassName),
                id: this._id,
                style: {
                  height,
                  width
                },
                children: children
              })]
            }))
          );
        }
      }))
    }));
  }
}
UISelectableBox.propTypes = {
  active: hidden(PropTypes.bool),
  'aria-checked': PropTypes.bool,
  block: PropTypes.bool,
  children: PropTypes.node.isRequired,
  disabled: PropTypes.bool.isRequired,
  flush: PropTypes.bool,
  height: PropTypes.number,
  hovered: hidden(PropTypes.bool),
  innerClassName: PropTypes.string,
  name: PropTypes.string,
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onTouchStart: PropTypes.func,
  onTouchEnd: PropTypes.func,
  /**
   * A `Controllable` function that is called when the `selected` value changes
   * in non-`radio` `type`s. To control `radio`s, use `onClick` instead.
   */
  onSelectedChange: PropTypes.func,
  padded: PropTypes.bool,
  readOnly: PropTypes.bool,
  selected: PropTypes.bool,
  selectionMarkLocation: PropTypes.oneOf(['left', 'top-right']),
  type: PropTypes.oneOf(['default', 'checkbox', 'radio']).isRequired,
  width: PropTypes.number
};
UISelectableBox.defaultProps = {
  disabled: false,
  padded: true,
  readOnly: false,
  selected: false,
  type: 'default'
};
UISelectableBox.displayName = 'UISelectableBox';
export default Controllable(UISelectableBox, ['selected']);